import { useEffect, useState } from "react";
import { Bell, HelpCircle, LogOut } from "lucide-react";
import toast from "react-hot-toast";
import { useAuthStore } from "../../store/useAuthStore";
import { roleLabel, roleBadgeClass } from "../../lib/role";
import { axiosInstance } from "../../lib/axios";

// The freelancer profile the client hired (freelance account name + picture).
// Cached per session so every portal page shows it without refetching.
const usePortalAccountManager = () => {
  const [am, setAm] = useState<{ name: string; profilePic?: string } | null>(() => {
    try {
      const raw = sessionStorage.getItem("cre-account-manager");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  useEffect(() => {
    if (am) return;
    axiosInstance
      .get("/client-portal/account-manager")
      .then(({ data }) => {
        if (!data?.accountManager) return;
        setAm(data.accountManager);
        try { sessionStorage.setItem("cre-account-manager", JSON.stringify(data.accountManager)); } catch { /* optional */ }
      })
      .catch(() => { /* identity is a nicety — never block the page */ });
  }, [am]);
  return am;
};

// Shared Client Workspace top bar — consistent across every portal page
// (Dashboard, Services, Invoices, Files, Inbox). Title on the left; bell / help
// / profile menu on the right.
export default function ClientTopBar({ title = "" }: { title?: string }) {
  const { authUser, logout }: any = useAuthStore();
  const [open, setOpen] = useState(false);
  const soon = () => toast("This section is coming soon.", { icon: "🔜" });
  const initial = (authUser?.fullName || authUser?.email || "U").slice(0, 1).toUpperCase();
  const accountManager = usePortalAccountManager();

  return (
    <header className="h-16 shrink-0 bg-white border-b border-outline-variant px-4 lg:px-8 flex items-center justify-between gap-2">
      <h1 className="text-2xl font-bold text-on-surface truncate">{title}</h1>
      <div className="flex items-center gap-2 shrink-0">
        {accountManager && (
          <div
            className="hidden sm:flex items-center gap-2 pr-3 mr-1 border-r border-outline-variant"
            title={`You are working with ${accountManager.name}`}
          >
            {accountManager.profilePic ? (
              <img src={accountManager.profilePic} alt={accountManager.name} className="h-8 w-8 rounded-full object-cover" />
            ) : (
              <div className="h-8 w-8 rounded-full bg-surface-container-high grid place-items-center text-[11px] font-bold text-on-surface-variant">
                {accountManager.name.slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="leading-tight">
              <p className="text-[12px] font-bold text-on-surface">{accountManager.name}</p>
              <p className="text-[10px] text-on-surface-variant">Your account manager</p>
            </div>
          </div>
        )}
        <button onClick={soon} title="Notifications" className="p-2 rounded-full text-on-surface-variant hover:bg-surface-container-high"><Bell size={19} /></button>
        <button onClick={soon} title="Support" className="p-2 rounded-full text-on-surface-variant hover:bg-surface-container-high"><HelpCircle size={19} /></button>
        <div className="relative">
          <button onClick={() => setOpen((v) => !v)} className="h-9 w-9 rounded-full bg-primary-container text-white grid place-items-center font-bold hover:bg-primary transition-colors">
            {initial}
          </button>
          {open && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
              <div className="absolute right-0 top-11 w-60 bg-white rounded-xl border border-outline-variant shadow-lg z-30 overflow-hidden">
                <div className="px-4 py-3 border-b border-outline-variant">
                  <p className="text-[13px] font-bold text-on-surface truncate">{authUser?.fullName || "Client"}</p>
                  <p className="text-[11px] text-on-surface-variant truncate">{authUser?.email}</p>
                  <span className={`mt-1.5 inline-block text-[10px] font-bold px-2 py-0.5 rounded-full ring-1 ${roleBadgeClass(authUser?.role)}`}>
                    {roleLabel(authUser?.role)}
                  </span>
                </div>
                <button onClick={logout} className="w-full flex items-center gap-2 px-4 py-3 text-[13px] text-error hover:bg-error-container/40"><LogOut size={15} /> Sign out</button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
